import React from 'react';
import { MapPin, Phone, Mail, User, ExternalLink } from 'lucide-react';
import { BookingDetails } from '@/hooks/booking/useBookingDetails';
import { useMapUrl } from '@/hooks/useMapUrl';
import { MapPreview } from '@/components/ui/MapPreview';
import { Separator } from '@/components/ui/separator';

interface CustomerInfoCardProps {
  booking: BookingDetails;
}

export const CustomerInfoCard = ({ booking }: CustomerInfoCardProps) => {
  // Use map pin if available, otherwise use address
  const mapLocation = booking.customer_map_link || booking.customer_address;
  const { mapUrl, mapEmbedUrl, isLoadingKey, mapsApiKey } = useMapUrl(mapLocation || null);

  const getGoogleMapsUrl = (address?: string, mapLink?: string) => {
    if (mapLink) {
      return mapLink;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5" />
          Customer Information
        </h2>
      </div>

      {/* Customer Name */}
      {booking.customer.name && (
        <div className="text-center">
          <h3 className="text-xl font-semibold text-foreground">
            {booking.customer.name}
          </h3>
        </div>
      )}

      <Separator />

      {/* Contact Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">Contact Information</h4>
        
        <div className="grid grid-cols-1 gap-4">
          <div className="flex items-center gap-3">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Email</p>
              <p className="text-sm text-muted-foreground">
                {booking.customer.email}
              </p>
            </div>
          </div>

          {booking.customer.phone && (
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Phone</p>
                <p className="text-sm text-muted-foreground">
                  {booking.customer.phone}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Location Information */}
      {(booking.customer_address || booking.customer_map_link) && (
        <>
          <Separator />
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Location</h4>
            
            {booking.customer_address && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Address</p>
                  <p className="text-sm text-muted-foreground">
                    {booking.customer_address}
                  </p>
                </div>
              </div>
            )}

            {/* Map Link */}
            {getGoogleMapsUrl(booking.customer_address, booking.customer_map_link) && (
              <a 
                href={getGoogleMapsUrl(booking.customer_address, booking.customer_map_link)!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Open in Google Maps
              </a>
            )}

            {/* Embedded Map */}
            <MapPreview
              address={mapLocation!}
              mapEmbedUrl={mapEmbedUrl}
              isLoadingKey={isLoadingKey}
              mapsApiKey={mapsApiKey}
            />

            {/* Directions */}
            {booking.customer.directions && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Additional Directions</p>
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md">
                  {booking.customer.directions}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};